import { Box, Typography, useTheme } from '@mui/material';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import type { AppState } from '@store/store';
import React from 'react';
import { useSelector } from 'react-redux';

interface LoadingBoxProps {
    text?: string;
}

/**
 * LoadingBox component.
 *
 * @param {LoadingBoxProps} props - The props for the LoadingBox component.
 * @returns {JSX.Element} The LoadingBox component.
 */
function LoadingBox(props: LoadingBoxProps) {
    const theme = useTheme();
    const lang = useSelector((state: AppState) => state.employee.language);
    const loadingText = getLanguageTexts(lang).loading;
    const text = props.text ? props.text : loadingText;

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2.5,
                justifyContent: 'center',
                mt: 2.5,
            }}
        >
            <Box
                sx={{
                    position: 'relative',
                    width: 80,
                    height: 80,
                    '& > div': {
                        position: 'absolute',
                        width: 64,
                        height: 64,
                        margin: 1,
                        border: '8px solid transparent',
                        borderRadius: '50%',
                        animation: 'lds-ring 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite',
                        '&:nth-of-type(1)': {
                            borderTopColor: theme.palette.primary.main,
                            animationDelay: '-0.45s',
                        },
                        '&:nth-of-type(2)': {
                            borderTopColor: theme.palette.secondary.main,
                            animationDelay: '-0.3s',
                        },
                        '&:nth-of-type(3)': {
                            borderTopColor: theme.palette.primary.light,
                            animationDelay: '-0.15s',
                        },
                        '&:nth-of-type(4)': {
                            borderTopColor: theme.palette.secondary.light,
                            animationDelay: '0s',
                        },
                    },
                    '@keyframes lds-ring': {
                        '0%': {
                            transform: 'rotate(0deg)',
                        },
                        '100%': {
                            transform: 'rotate(360deg)',
                        },
                    },
                }}
            >
                <div />
                <div />
                <div />
                <div />
            </Box>
            <Typography
                variant="body2"
                sx={{
                    textAlign: 'center',
                    fontSize: '1.1875rem',
                    color: theme.palette.text.primary,
                }}
            >
                {text}
            </Typography>
        </Box>
    );
}

export default LoadingBox;
